// src/pages/Wallet/LevelsInfoPage.jsx - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './LevelsInfoPage.css';

const LevelsInfoPage = () => {
  const navigate = useNavigate();
  const { formatCurrency, currency, getCurrencySymbol } = useCurrency();
  const [userLevel, setUserLevel] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Level thresholds in NGN (base currency)
  const LEVEL_THRESHOLDS = [
    { level: 1, min: 0 },
    { level: 2, min: 300000 },
    { level: 3, min: 1000000 },
    { level: 4, min: 5000000 },
    { level: 5, min: 15000000 }
  ];

  useEffect(() => {
    fetchUserWallet();
    fetchExchangeRate();
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('💱 Exchange rate API response:', data);
        setExchangeRate(data);
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const convertNGNtoUSD = (amountNGN) => {
    if (!exchangeRate) {
      return amountNGN / 1600;
    }
    
    if (exchangeRate.usd_to_ngn) {
      const rate = parseFloat(exchangeRate.usd_to_ngn);
      return amountNGN / rate;
    } else if (exchangeRate.ngn_to_usd) {
      const rate = parseFloat(exchangeRate.ngn_to_usd);
      return amountNGN * rate;
    }
    
    return amountNGN / 1600;
  };

  const convertUSDtoNGN = (amountUSD) => {
    if (!exchangeRate) {
      return amountUSD * 1600;
    }
    
    if (exchangeRate.usd_to_ngn) {
      const rate = parseFloat(exchangeRate.usd_to_ngn);
      return amountUSD * rate;
    } else if (exchangeRate.ngn_to_usd) {
      const rate = parseFloat(exchangeRate.ngn_to_usd);
      return amountUSD / rate;
    }
    
    return amountUSD * 1600;
  };

  // Calculate level based on balance (fallback if API doesn't provide level)
  const calculateLevelFromBalance = (balanceNGN) => {
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (balanceNGN >= LEVEL_THRESHOLDS[i].min) {
        return LEVEL_THRESHOLDS[i].level;
      }
    }
    return 1; // Default to level 1
  };

  const formatRequirement = (amountNGN) => {
    if (currency === 'USD') {
      const usdAmount = convertNGNtoUSD(amountNGN);
      return `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}+`;
    }
    return `₦${amountNGN.toLocaleString('en-NG')}+`;
  };

  const formatAmount = (amountNGN) => {
    if (currency === 'USD') {
      const usdAmount = convertNGNtoUSD(amountNGN);
      return `$${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₦${amountNGN.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchUserWallet = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/wallet/balance/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('👛 Full Wallet API Response:', data);
        console.log('👛 Level from API:', data.level);
        console.log('👛 Balance from API:', data.balance);
        console.log('👛 Currency from API:', data.currency);
        
        // Get balance (already in user's preferred currency from API)
        const balanceAmount = parseFloat(data.balance) || 0;
        setCurrentBalance(balanceAmount);
        
        // Convert balance to NGN for level calculation if needed
        let balanceInNGN = balanceAmount;
        if (data.currency === 'USD' || currency === 'USD') {
          balanceInNGN = convertUSDtoNGN(balanceAmount);
        }
        
        // Use level from API if available, otherwise calculate it
        let detectedLevel;
        if (data.level !== undefined && data.level !== null) {
          detectedLevel = parseInt(data.level);
          console.log('✅ Using level from API:', detectedLevel);
        } else {
          detectedLevel = calculateLevelFromBalance(balanceInNGN);
          console.log('⚠️ API did not return level, calculated from balance:', detectedLevel);
          console.log('   Balance in NGN:', balanceInNGN);
        }
        
        setUserLevel(detectedLevel);
        console.log('🎯 Final user level set to:', detectedLevel);
      } else {
        console.error('❌ Failed to fetch wallet balance, status:', response.status);
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        // Set default values
        setUserLevel(1);
        setCurrentBalance(0);
      }
    } catch (error) {
      console.error('❌ Error fetching wallet:', error);
      // Set default values
      setUserLevel(1);
      setCurrentBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const levels = [
    {
      level: 1,
      name: 'Observer',
      requirement: 'Any amount',
      requirementAmount: 0,
      color: '#94a3b8',
      icon: '🌱',
      privileges: [
        'Access to Listed Assets (Core NG Assets)',
        'Basic trading features',
        'Access to Normal Savings Plans',
        'Wallet Funding & withdrawal',
        'Transaction history tracking',
        'Referral program participation'
      ],
      restrictions: [
        'No access to PRIVATE EQUITY ASSETS',
        'No access to SCALE UP ASSETS',
        'No access to GROWTH ROUND ASSETS',
        'No access to Investment Plans (requires Level 2+)'
      ]
    },
    {
      level: 2,
      name: 'Investor',
      requirement: '300,000',
      requirementAmount: 300000,
      color: '#cd7f32',
      icon: '🥉',
      privileges: [
        'All Level 1 privileges',
        'Access to GROWTH ROUND ASSETS',
        'Access to Investment Plans',
        'Customized investment strategies',
        'Lock-in investment options',
        'Flexible withdrawal preferences',
        'Enhanced portfolio tracking',
        'Priority customer support',
        'Weekly market insights',
        'Advanced trading analytics'
      ],
      restrictions: [
        'No access to PRIVATE EQUITY ASSETS',
        'No access to SCALE UP ASSETS'
      ]
    },
    {
      level: 3,
      name: 'Alpha',
      requirement: '1,000,000',
      requirementAmount: 1000000,
      color: '#a78bfa',
      icon: '🥈',
      privileges: [
        'All Level 2 privileges',
        'Access to GROWTH ROUND ASSETS',
        'Early access to new listings',
        'Dedicated account manager',
        'Monthly performance reports',
        'Investment strategy consultation',
        'Reduced transaction fees'
      ],
      restrictions: [
        'No access to PRIVATE EQUITY ASSETS'
      ]
    },
    {
      level: 4,
      name: 'Institutional',
      requirement: '5,000,000',
      requirementAmount: 5000000,
      color: '#f59e0b',
      icon: '🥇',
      privileges: [
        'All Level 3 privileges',
        'Access to PRIVATE EQUITY ASSETS',
        'Exclusive investment opportunities',
        'VIP customer support',
        'Real-time market alerts',
        'Advanced portfolio analytics',
        'Minimum transaction fees',
        'Direct line to investment advisors'
      ],
      restrictions: []
    },
    {
      level: 5,
      name: 'Platinum',
      requirement: '15,000,000',
      requirementAmount: 15000000,
      color: '#10b981',
      icon: '💎',
      privileges: [
        'All Level 4 privileges',
        'Premium investment advisory',
        'Quarterly investment reviews',
        'Priority access to ASSETS',
        'Institutional-grade analytics',
        'Zero transaction fees on investments',
        'White-glove concierge service',
        'Exclusive market research reports'
      ],
      restrictions: []
    }
  ];

  const getProgressToNextLevel = () => {
    if (!userLevel || userLevel >= 5) return null;
    
    const nextLevel = levels[userLevel];
    const nextLevelAmount = nextLevel.requirementAmount;
    
    // Convert current balance to NGN for comparison with thresholds
    let currentBalanceNGN = currentBalance;
    if (currency === 'USD') {
      currentBalanceNGN = convertUSDtoNGN(currentBalance);
    }
    
    const progress = (currentBalanceNGN / nextLevelAmount) * 100;
    const neededNGN = Math.max(0, nextLevelAmount - currentBalanceNGN);
    
    console.log('📈 Progress Calculation:', {
      currency,
      currentBalance,
      currentBalanceNGN,
      nextLevelAmount,
      progress,
      neededNGN,
      neededUSD: currency === 'USD' ? convertNGNtoUSD(neededNGN) : null
    });
    
    return {
      nextLevel: nextLevel.level,
      nextLevelName: nextLevel.name,
      needed: neededNGN,
      progress: Math.min(progress, 100),
      nextLevelAmount
    };
  };

  const progressData = getProgressToNextLevel();

  // Show loading state
  if (loading) {
    return (
      <div className="levels-info-page">
        <div className="levels-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div className="header-content">
            <h1>Account Levels & Privileges</h1>
            <p>Loading your account information...</p>
          </div>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="levels-info-page">
      <div className="levels-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className="header-content">
          <h1>Account Levels & Privileges</h1>
          <p>Unlock more features as your balance grows</p>
        </div>
      </div>

      {userLevel !== null && (
        <div className="current-level-card">
          <div className="current-level-header">
            <div className="level-icon">{levels[userLevel - 1].icon}</div>
            <div>
              <h2>Your Current Level</h2>
              <div 
                className="level-badge-large"
                style={{ backgroundColor: levels[userLevel - 1].color }}
              >
                Level {userLevel} - {levels[userLevel - 1].name}
              </div>
            </div>
          </div>
          
          <div className="balance-info">
            <div className="balance-item">
              <span className="label">Current Balance</span>
              <span className="value">{formatCurrency(currentBalance)}</span>
            </div>
            <div className="balance-item">
              <span className="label">Level Requirement</span>
              <span className="value">{formatRequirement(levels[userLevel - 1].requirementAmount)}</span>
            </div>
          </div>

          {progressData && (
            <div className="next-level-progress">
              <div className="progress-header">
                <span>Progress to Level {progressData.nextLevel}</span>
                <span className="progress-percentage">{progressData.progress.toFixed(1)}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${progressData.progress}%`,
                    backgroundColor: levels[progressData.nextLevel - 1].color
                  }}
                />
              </div>
              <p className="progress-text">
                Add <strong>{formatAmount(progressData.needed)}</strong> more to unlock {progressData.nextLevelName}
              </p>
            </div>
          )}

          <button 
            className="deposit-action-btn"
            onClick={() => navigate('/wallet/deposit')}
          >
            💰 Deposit to Upgrade
          </button>
        </div>
      )}

      <div className="levels-grid">
        <h2 className="section-title">All Account Levels</h2>
        
        {levels.map((level) => {
          const isCurrentLevel = userLevel === level.level;
          const isUnlocked = userLevel >= level.level;
          const isNextLevel = userLevel === level.level - 1;

          return (
            <div 
              key={level.level}
              className={`level-card ${isCurrentLevel ? 'current' : ''} ${isUnlocked ? 'unlocked' : 'locked'} ${isNextLevel ? 'next' : ''}`}
            >
              <div className="level-card-header" style={{ borderColor: level.color }}>
                <div className="level-icon-large">{level.icon}</div>
                <div className="level-info">
                  <div 
                    className="level-badge"
                    style={{ backgroundColor: level.color }}
                  >
                    Level {level.level}
                  </div>
                  <h3>{level.name}</h3>
                  <p className="requirement">{formatRequirement(level.requirementAmount)} Current Balance</p>
                </div>
                {isCurrentLevel && (
                  <div className="current-badge">Your Level</div>
                )}
                {isUnlocked && !isCurrentLevel && (
                  <div className="unlocked-badge">✓ Unlocked</div>
                )}
                {!isUnlocked && (
                  <div className="locked-badge">🔒 Locked</div>
                )}
              </div>

              <div className="level-card-body">
                <div className="privileges-section">
                  <h4>✨ Privileges</h4>
                  <ul className="privileges-list">
                    {level.privileges.map((privilege, index) => (
                      <li key={index}>
                        <span className="check-icon">✓</span>
                        {privilege}
                      </li>
                    ))}
                  </ul>
                </div>

                {level.restrictions.length > 0 && (
                  <div className="restrictions-section">
                    <h4>⚠️ Restrictions</h4>
                    <ul className="restrictions-list">
                      {level.restrictions.map((restriction, index) => (
                        <li key={index}>
                          <span className="cross-icon">✗</span>
                          {restriction}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!isUnlocked && (
                  <div className="level-action">
                    <button 
                      className="unlock-btn"
                      style={{ backgroundColor: level.color }}
                      onClick={() => navigate('/wallet/deposit')}
                    >
                      Deposit to Unlock
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="important-notes">
        <h3>📋 Important Information</h3>
        <div className="notes-grid">
          <div className="note-card">
            <div className="note-icon">💰</div>
            <h4>Level Based on Current Balance</h4>
            <p>Your level is determined by your <strong>current wallet balance</strong>, not total deposits. Keep funds in your wallet to maintain level benefits.</p>
          </div>
          
          <div className="note-card">
            <div className="note-icon">📈</div>
            <h4>Normal Income Plans</h4>
            <p><strong>All levels</strong> can access Normal Income Plans with 0.38% projected weekly returns. No minimum level required!</p>
          </div>
          
          <div className="note-card">
            <div className="note-icon">💎</div>
            <h4>Investment Plans (Level 2+)</h4>
            <p>Investment Plans with lock-in periods and customized strategies require <strong>Level 2 ({formatRequirement(300000)})</strong>. This includes investments in Assets with flexible withdrawal options.</p>
          </div>
          
          <div className="note-card">
            <div className="note-icon">🎯</div>
            <h4>Secondary Market Trading Requirements</h4>
            <p>
              • <strong>CORE NG ASSETS:</strong> Level 1+ (Any amount)<br/>
              • <strong>GROWTH ROUND ASSETS:</strong> Level 2+ ({formatRequirement(300000)})<br/>
              • <strong>SCALE UP ASSETS:</strong> Level 3+ ({formatRequirement(1000000)})<br/>
              • <strong>PRIVATE EQUITY ASSETS:</strong> Level 4+ ({formatRequirement(5000000)})
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelsInfoPage;